import { PaletteMode } from '@mui/material';

/**
 * The semantic styling token layer — the single source of truth for every
 * intent-named color in the app. Both the light and the dark theme are built
 * from `semanticTokensFor(mode)`, so a component that draws from these tokens
 * (via `theme.semantic.*` or the mapped MUI `background.*` / `text.*` tokens)
 * adapts to the active mode for free.
 *
 * Rules:
 * - Name tokens by INTENT (`surfaceRaised`, `textSecondary`, `statusError`),
 *   never by value (`gray7`, `navy`). The value is allowed to differ per mode;
 *   the intent is not.
 * - Light values MUST equal the colors the app ships today, so enabling the
 *   theme factory is a no-op in light mode. The dark column is the new work.
 * - Brand-fixed colors (the Tread yellow, brand marks) are NOT semantic tokens
 *   — read them from `theme.palette.primary` / `theme.brandV2` so they stay
 *   constant across modes.
 */
export const PALETTE_ERROR_MAIN = '#D32F2F';

export interface SemanticColorTokens {
  // Surfaces (lowest → highest elevation)
  surface: string;
  surfaceSunken: string;
  surfaceRaised: string;
  surfaceOverlay: string;
  surfacePage: string;
  /** Nested table levels, each lighter in dark; white in light, as a tint fails 4.5:1 contrast. */
  surfaceContainer: string;
  surfaceContainerHigh: string;
  /** An expanded table row and its panel, darker so the nested table inside reads as lifted. */
  surfaceExpanded: string;
  /** Translucent scrim laid over content while it is busy (loading overlays). */
  surfaceScrim: string;
  /**
   * Fill behind a text input, select or autocomplete.
   *
   * `transparent` in light so an input keeps showing whatever panel it sits on — that is
   * what MUI's outlined input already renders, so light mode is untouched. Dark needs a
   * real value: with a transparent fill the dialog paper, the panel and the field all
   * resolve to the same color and a form reads as one undifferentiated slab. This sits
   * below the base surface, so a field reads as an inset well on the page (1.06:1) and
   * clearly so on raised dialog paper (1.20:1).
   */
  surfaceInput: string;
  /**
   * The same input well, but opaque in light.
   *
   * `surfaceInput` is transparent in light precisely because that is what MUI's outlined
   * input already computes, which makes the theme-level override a guaranteed no-op there.
   * Hand-built fields are the opposite case: they paint their own fill, several of them sit
   * on tinted panels or hover-coloured rows, and going transparent would let the container
   * bleed through a control that is supposed to read as a white well. They take this token
   * instead, so light keeps its opaque white and dark still matches every other field.
   */
  surfaceInputSolid: string;
  /** Disabled input fill. In dark it merges into the base surface, so the field stops reading as editable. */
  surfaceInputDisabled: string;
  // Text / ink
  textPrimary: string;
  textSecondary: string;
  textDisabled: string;
  textInverse: string;
  /** Maximum-contrast ink for selected/active labels (tab selected, etc.) */
  textStrong: string;
  /** Ink that sits on the brand (yellow) surface — black in both modes. */
  textOnBrand: string;
  /** Focused input label color. */
  inputFocusLabel: string;
  // Lines
  border: string;
  borderStrong: string;
  divider: string;
  // Interaction surfaces
  actionHover: string;
  actionSelected: string;
  actionDisabledBg: string;
  /** ListItemButton hover wash (brand-yellow tint). */
  listItemHoverBg: string;
  // Status — foreground/emphasis + subtle background pair
  statusSuccess: string;
  statusSuccessBg: string;
  statusWarning: string;
  statusWarningBg: string;
  statusError: string;
  statusErrorBg: string;
  statusInfo: string;
  statusInfoBg: string;
  /** Background wash behind a search-match / highlighted text run. */
  searchHighlightBg: string;
  // Chip (outlined) variants
  chipOutlinedBg: string;
  chipNeutralBg: string;
  chipNeutralBorder: string;
  chipNeutralText: string;
  chipBrandBg: string;
  chipBrandBorder: string;
  chipBrandText: string;
  chipDashedBg: string;
  chipDashedBorder: string;
  /**
   * Categorical chip fills — used to distinguish *kinds* of thing (a rate card's pickup
   * site vs. material vs. equipment type), not severity. Deliberately more saturated than
   * the `status*Bg` washes, which are too faint to read as chips.
   */
  chipInfoBg: string;
  chipSuccessBg: string;
  chipWarningBg: string;
  /**
   * Full-width table-row wash for a flagged (potential duplicate) load. Matches the
   * warning chip wash in light; in dark a translucent wash is too faint to read across a
   * whole row over the dark surface, so this is a solid maroon instead.
   */
  rowFlaggedBg: string;
  rowCriticalBg: string;
  rowCriticalText: string;
  rowWarningBg: string;
  rowWarningText: string;
  /**
   * Foreground for text that behaves like a link (in-app affordances, not `<a href>`).
   *
   * Neither `brandV2.colors.treadBlue` nor `palette.info.main` works for this: both are
   * single fixed values, and on the dark surface they land at ~3.9:1 and ~3.6:1 — under the
   * 4.5:1 AA floor for body text. These values clear it in both modes.
   */
  linkText: string;
  /** Error copy: `palette.error.main` falls to ~3.6:1 on the dark surface and `statusError` to ~3.4:1 on white. */
  errorText: string;
  /** Brand yellow, reserved as a solid fill for impersonation and other admin-only surfaces. Fixed across modes so the state reads the same in either theme. */
  elevatedState: string;
  elevatedStateBg: string;
  elevatedStateBorder: string;
  elevatedStateInk: string;
  elevatedStateInkMuted: string;
  /**
   * The dark status-toast surface (preparing / files-ready toasters) and the inks on it.
   * Fixed across modes: following the neutral ramp in dark flips the toast into a pale
   * card where the brand-yellow download links become unreadable.
   */
  surfaceToast: string;
  /** Inset 1px edge (box-shadow, so no layout cost) that separates the toast from a dark page; invisible in light where the black toast already contrasts. */
  borderToast: string;
  textOnToast: string;
  textOnToastSecondary: string;
  chartCategorical: string[];
}

const lightTokens: SemanticColorTokens = {
  surface: '#FFFFFF',
  surfaceSunken: '#F1F6F9', // treadGray8
  surfaceRaised: '#FFFFFF',
  surfaceOverlay: '#F8FBFC', // treadGray9
  surfacePage: '#E3E8EC',
  surfaceContainer: '#FFFFFF',
  surfaceContainerHigh: '#FFFFFF',
  surfaceExpanded: '#FFFDED', // primary.light
  surfaceScrim: 'rgba(255, 255, 255, 0.8)', // white @ 80% — the shipped loading-overlay value
  surfaceInput: 'transparent',
  surfaceInputSolid: '#FFFFFF',
  surfaceInputDisabled: 'transparent',
  textPrimary: '#132732', // treadBlack
  textSecondary: '#697982', // treadGray3
  textDisabled: 'rgba(0, 0, 0, 0.38)',
  textInverse: '#FFFFFF',
  textStrong: '#000000',
  textOnBrand: '#000000',
  inputFocusLabel: '#252135', // secondary.main
  border: '#EAECF0',
  borderStrong: '#D6DDE1', // treadGray7
  divider: '#EAECF0',
  actionHover: 'rgba(0, 0, 0, 0.04)',
  actionSelected: 'rgba(0, 0, 0, 0.08)',
  actionDisabledBg: 'rgba(0, 0, 0, 0.12)',
  listItemHoverBg: '#FFE5000D', // brand yellow @ 5% alpha
  statusSuccess: '#2E7D32',
  statusSuccessBg: 'rgba(46, 125, 50, 0.04)',
  statusWarning: '#ED6C02',
  statusWarningBg: 'rgba(211, 47, 47, 0.10)',
  statusError: '#DF6666', // status-indicator red (softer than palette error.main #D32F2F)
  statusErrorBg: 'rgba(251, 234, 234, 0.30)',
  statusInfo: '#0288D1',
  statusInfoBg: 'rgba(2, 136, 209, 0.08)',
  searchHighlightBg: '#FFF7B0',
  chipOutlinedBg: '#E0E0E0',
  chipNeutralBg: 'rgba(37, 33, 53, 0.04)',
  chipNeutralBorder: 'rgba(37, 33, 53, 0.30)',
  chipNeutralText: '#1C1C1C',
  chipBrandBg: '#FFFDEB',
  chipBrandBorder: '#D9A64D',
  chipBrandText: '#4B3525',
  chipDashedBg: '#F5F5F5',
  chipDashedBorder: '#BDBDBD',
  // Matches the saturation of the rate-card editor's condition chips, so the same condition
  // reads the same whether you are editing a card or referencing one.
  chipInfoBg: '#E3F2FD',
  chipSuccessBg: '#E8F5E9',
  chipWarningBg: '#FFF3E0',
  rowFlaggedBg: '#FFF3E0',
  rowCriticalBg: 'rgba(234, 18, 0, 0.08)',
  rowCriticalText: '#B80E00',
  rowWarningBg: 'rgba(255, 170, 19, 0.12)',
  rowWarningText: '#8F5C00',
  linkText: '#0074DF',
  errorText: PALETTE_ERROR_MAIN,
  elevatedState: '#FFE500',
  elevatedStateBg: '#FFE500',
  elevatedStateBorder: '#ECC600',
  elevatedStateInk: '#132732',
  elevatedStateInkMuted: '#4B3525',
  surfaceToast: '#132732', // treadBlack
  borderToast: 'transparent',
  textOnToast: '#FFFFFF',
  textOnToastSecondary: '#D6DDE1', // treadGray7
  // Tailwind 500 shades: blue, emerald, amber, violet, cyan, orange, fuchsia, rose.
  chartCategorical: [
    '#3b82f6',
    '#10b981',
    '#f59e0b',
    '#8b5cf6',
    '#06b6d4',
    '#f97316',
    '#d946ef',
    '#f43f5e',
  ],
};

const darkTokens: SemanticColorTokens = {
  // Base dark surface is the Tread brand "preferred black" (#0A1820), verified
  // against the marketing brand foundation (tread-ai-website global.css .surface-dark).
  // Elevation ramps lighter from there: raised (cards) → overlay (menus); sunken is
  // the recessed well beneath the base. Sunken sits about as far below the base as
  // raised sits above it, so recessed wells and zebra striping read as a distinct
  // step in dark mode (the previous #06121A was only ~1.05:1 vs the base — invisible).
  surface: '#0A1820',
  surfaceSunken: '#030C14',
  surfaceRaised: '#13242E',
  surfaceOverlay: '#1B3038',
  surfacePage: '#030C14',
  surfaceContainer: '#152733',
  // The lightest that keeps the treadBlue2 driver names at 4.5:1.
  surfaceContainerHigh: '#192E3A',
  surfaceExpanded: '#0A1820',
  surfaceScrim: 'rgba(10, 24, 32, 0.8)', // base surface (#0A1820) @ 80% — dims the dark surface
  surfaceInput: '#06111A',
  surfaceInputSolid: '#06111A',
  surfaceInputDisabled: '#0A1820',
  textPrimary: '#E6EDF1',
  textSecondary: '#A0ABB1', // treadGray5
  textDisabled: 'rgba(255, 255, 255, 0.38)',
  textInverse: '#132732',
  textStrong: '#FFFFFF',
  textOnBrand: '#000000',
  inputFocusLabel: '#E6EDF1',
  border: 'rgba(255, 255, 255, 0.12)',
  borderStrong: 'rgba(255, 255, 255, 0.24)',
  divider: 'rgba(255, 255, 255, 0.12)',
  actionHover: 'rgba(255, 255, 255, 0.08)',
  actionSelected: 'rgba(255, 255, 255, 0.16)',
  actionDisabledBg: 'rgba(255, 255, 255, 0.12)',
  listItemHoverBg: 'rgba(255, 229, 0, 0.12)',
  statusSuccess: '#66BB6A',
  statusSuccessBg: 'rgba(102, 187, 106, 0.16)',
  statusWarning: '#FFA726',
  statusWarningBg: 'rgba(255, 167, 38, 0.20)',
  statusError: '#F26D6D',
  statusErrorBg: 'rgba(242, 109, 109, 0.20)',
  statusInfo: '#4FC3F7',
  statusInfoBg: 'rgba(79, 195, 247, 0.16)',
  searchHighlightBg: 'rgba(255, 229, 0, 0.24)',
  chipOutlinedBg: 'rgba(255, 255, 255, 0.08)',
  chipNeutralBg: 'rgba(255, 255, 255, 0.06)',
  chipNeutralBorder: 'rgba(255, 255, 255, 0.30)',
  chipNeutralText: '#E6EDF1',
  chipBrandBg: 'rgba(255, 229, 0, 0.12)',
  chipBrandBorder: '#D9A64D',
  chipBrandText: '#F2D9A6',
  // Same hues as light mode but as translucent washes over the dark surface, so the chips
  // stay distinguishable without the pastel fills that would glare against it.
  chipInfoBg: 'rgba(79, 195, 247, 0.22)',
  chipSuccessBg: 'rgba(102, 187, 106, 0.22)',
  chipWarningBg: 'rgba(255, 167, 38, 0.22)',
  rowFlaggedBg: '#2B0000',
  rowCriticalBg: 'rgba(234, 18, 0, 0.16)',
  rowCriticalText: '#FFA39C',
  rowWarningBg: 'rgba(255, 170, 19, 0.12)',
  rowWarningText: '#FFAA13',
  linkText: '#4FC3F7',
  errorText: '#F26D6D',
  chipDashedBg: 'rgba(255, 255, 255, 0.06)',
  chipDashedBorder: 'rgba(255, 255, 255, 0.30)',
  elevatedState: '#FFE500',
  elevatedStateBg: '#FFE500',
  elevatedStateBorder: '#ECC600',
  elevatedStateInk: '#132732',
  elevatedStateInkMuted: '#4B3525',
  // Deepest rung of the dark ladder (surfaceSunken): treadBlack's dark value is one
  // step above the page and the toast read as a flat patch on it.
  surfaceToast: '#030C14',
  borderToast: 'rgba(255, 255, 255, 0.16)',
  textOnToast: '#FFFFFF',
  textOnToastSecondary: '#D6DDE1', // treadGray7
  // The matching Tailwind 400 shades, which read better on the dark surface.
  chartCategorical: [
    '#60a5fa',
    '#34d399',
    '#fbbf24',
    '#a78bfa',
    '#22d3ee',
    '#fb923c',
    '#e879f9',
    '#fb7185',
  ],
};

export function semanticTokensFor(mode: PaletteMode) {
  return mode === 'dark' ? darkTokens : lightTokens;
}
